import React from 'react';
import { Container } from 'components/Containers';
import { useSelector } from 'react-redux';
import { styled } from '@mui/system';

import DarkCover from 'components/DarkCover';

import { useOnlineOrderingDisplayLocationInformationOnLayout } from 'hooks/useDisplayLocationInformationOnLayout';
import LogoFocusedLocationInformation from 'bento-ordering/menus/components/LogoFocusedLocationInformation';
import selectHeroImage from 'bento-ordering/base/selectors/selectHeroImage';
import useLocationInformation from 'bento-ordering/base/hooks/useLocationInformation';
import BaseScreenHero from 'bento-ordering/base/components/BaseScreenHero';
import InfoBar, { InfoBarWithLogo } from 'components/InfoBar';
import Avatar, { SQUARE } from 'components/Avatar';
import { getHasHeroImage } from 'helpers/getHasHeroImage';
import useAvatarPath from 'bento-ordering/base/hooks/useAvatarPath';
import { selectBrandLogo } from 'bento-ordering/brand/selectors';

const HALF_LOGO_HEIGHT = '90px';

const StyledContainer = styled(Container)(({ theme }) => ({
  '&.MuiContainer-root': {
    position: 'relative',
    bottom: theme.spacing(8),
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '1254px',
    [theme.breakpoints.up('md')]: {
      bottom: theme.spacing(5),
      justifyContent: 'flex-start',
      alignItems: 'start'
    },
    [theme.breakpoints.down('lg')]: {
      padding: '0 16px'
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: '20px'
    }
  }
}));

const StyledContainerWithLogo = styled(StyledContainer)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    bottom: HALF_LOGO_HEIGHT
  }
}));

function LogoFocusedScreenHero() {
  const logo = useSelector(selectBrandLogo);
  const heroImage = useSelector(selectHeroImage);
  const shouldDisplayLocationInformationOnLayout = useOnlineOrderingDisplayLocationInformationOnLayout();
  const avatarPath = useAvatarPath();
  const {
    location: { name },
    description
  } = useLocationInformation();

  const hasLocationDescription = !!description;
  const hasHeroImage = getHasHeroImage(heroImage);
  const hasLogo = !!logo;

  const ContainerComponent = hasLogo
    ? StyledContainerWithLogo
    : StyledContainer;
  const InfoBarComponent = hasLogo ? InfoBarWithLogo : InfoBar;
  const showLocationInformation = shouldDisplayLocationInformationOnLayout;

  return (
    <BaseScreenHero
      RootProps={{
        sx: theme => ({
          [theme.breakpoints.down('sm')]: {
            marginBottom: !showLocationInformation ? '-70px' : '0px'
          }
        })
      }}
      HeroContainerProps={{
        layout: 'logo-focused',
        hasHeroImage
      }}
      HeroProps={{
        image: heroImage
      }}
      heroContent={
        <DarkCover dataTestId="dark-cover" background={heroImage?.url ?? ''} />
      }
    >
      <ContainerComponent>
        <InfoBarComponent>
          {hasLogo && <Avatar src={logo} path={avatarPath} shape={SQUARE} />}
          <LogoFocusedLocationInformation
            hasLogo={hasLogo}
            hasLocationDescription={hasLocationDescription}
            name={name}
            showLocationInformation={showLocationInformation}
          />
        </InfoBarComponent>
      </ContainerComponent>
    </BaseScreenHero>
  );
}

export default LogoFocusedScreenHero;
